// Step 1 — Upload files + configure per-file specs.

import { useState } from "react";
import UploadDropzone, { UploadedFileRow } from "@/components/UploadDropzone";
import { ArrowRight, RotateCcw } from "lucide-react";
import { ORDER } from "@/constants/testIds";
import { SpecCard } from "./SpecCard";
import { TutorialLightbox, WatchTutorialButton } from "@/components/TutorialLightbox";

export function OrderStep1Upload({
  items,
  itemTotals,
  pricing,
  reorderHint,
  clearReorderHint,
  onUploaded,
  removeFile,
  updateSpec,
  updatePages,
  canProceed,
  goToDetails,
}) {
  const [tutorialOpen, setTutorialOpen] = useState(false);
  return (
    <div className="space-y-6">
      {reorderHint?.tracking && (
        <div className="border-l-4 border-[#059669] bg-[#059669]/10 p-4 flex items-start gap-3" data-testid="reorder-banner">
          <RotateCcw className="w-5 h-5 text-[#059669] mt-0.5" strokeWidth={2.5} />
          <div className="flex-1">
            <div className="font-bold text-sm">Reordering from {reorderHint.tracking}</div>
            <div className="text-xs text-[#525252] mt-0.5">
              Specs for {reorderHint.item_count} file{reorderHint.item_count === 1 ? "" : "s"} prefilled. {(reorderHint.items_template?.length || 0) > 0 ? `Drop ${reorderHint.items_template?.length} document${(reorderHint.items_template?.length || 0) === 1 ? "" : "s"} to apply.` : "Customer & delivery details are ready."}
            </div>
          </div>
          <button
            onClick={clearReorderHint}
            data-testid="reorder-banner-dismiss"
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] hover:text-[#0A0A0A]"
          >
            Clear
          </button>
        </div>
      )}
      <div>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Upload your documents
          </h1>
          <WatchTutorialButton onClick={() => setTutorialOpen(true)} className="mt-2" />
        </div>
        <p className="text-[#525252]">
          Supported: PDF, Word (.docx), JPG, PNG. PDF page count is exact; Word page count is estimated and editable.
        </p>
      </div>
      <UploadDropzone onUploaded={onUploaded} />
      <TutorialLightbox open={tutorialOpen} onClose={() => setTutorialOpen(false)} />

      {items.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] pt-4">
            § Print Specifications — {items.length} file{items.length === 1 ? "" : "s"}
          </h2>
          {items.map((x, idx) => (
            <UploadedFileRow key={x.file.id} file={x.file} onRemove={removeFile} onPagesChange={updatePages}>
              <SpecCard
                idx={idx}
                item={x}
                pricing={pricing}
                itemTotal={itemTotals[idx]}
                onUpdate={(patch) => updateSpec(x.file.id, patch)}
              />
            </UploadedFileRow>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="pt-4">
          <button
            data-testid={ORDER.proceedToCheckout}
            onClick={goToDetails}
            disabled={!canProceed}
            className="w-full bg-[#0A0A0A] text-white px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#059669] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            Continue to checkout <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
